/**
 * BudgetCard — phase 9 §B/§D budget surface, mounted in the Tonight tab
 * footer. GET/PUT /settings via api.ts: tokens-per-night cap, night window
 * times, pause-when-seat-limited toggle, per-session cap — plus the Tonight
 * budget progress bar (window usage vs cap).
 *
 * Usage source (§B2): when the plane returns a usage rollup on GET
 * /settings (usage.windowTokens) the bar binds to it; otherwise it
 * APPROXIMATES from the session rows the dashboard already polls — see
 * lib/budget.ts for the lower-bound caveats, which the card states.
 * Hidden entirely when GET /settings fails (older planes have no route).
 */
import { useCallback, useEffect, useState } from "react";
import {
  getSettings,
  machineRuntimes,
  putSettings,
  type Machine,
  type Runtime,
  type Session,
  type TenantSettings,
  type TenantSettingsRes,
} from "../lib/api";
import {
  DEFAULT_TIMEZONE,
  DEFAULT_WINDOW_END,
  DEFAULT_WINDOW_START,
  currentNightWindow,
  windowLabel,
  windowSessionTokens,
} from "../lib/budget";
import { useTenant } from "../lib/tenant";
import { compactCount } from "../lib/format";
import { Card, StatusPill, Toggle, Btn } from "../kit";
import { SecLabel } from "./DashShell";

const POLL_MS = 60_000;

/** "1,500,000" / "1.5m" / "800k" → tokens; "" → undefined (no cap);
 *  unparseable → null. */
function parseTokens(text: string): number | undefined | null {
  const t = text.replace(/[,_\s]/g, "").toLowerCase();
  if (!t) return undefined;
  const m = /^(\d+(?:\.\d+)?)([km])?$/.exec(t);
  if (!m) return null;
  const n = Number(m[1]) * (m[2] === "m" ? 1e6 : m[2] === "k" ? 1e3 : 1);
  return Math.round(n);
}

/** Phase 12: display order for the per-service usage split. */
const RUNTIME_ORDER: Runtime[] = ["claude", "codex", "any"];
const RUNTIME_LABEL: Record<Runtime, string> = {
  claude: "Claude",
  codex: "Codex",
  any: "Any",
};

export function BudgetCard({
  sessions,
  machines,
}: {
  sessions: Session[] | null;
  /** Used only to decide whether a Codex split is worth showing on a tenant
   *  with a codex-capable machine but no codex usage yet. */
  machines?: Machine[] | null;
}) {
  const { tenantId } = useTenant();
  const [loaded, setLoaded] = useState<TenantSettingsRes | null>(null);
  const [hidden, setHidden] = useState(false);

  // Edit buffer (seeded from the fetched settings).
  const [capText, setCapText] = useState("");
  const [winStart, setWinStart] = useState(DEFAULT_WINDOW_START);
  const [winEnd, setWinEnd] = useState(DEFAULT_WINDOW_END);
  const [pause, setPause] = useState(true);
  const [perSessionText, setPerSessionText] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const seed = useCallback((res: TenantSettingsRes) => {
    const s = res.settings;
    // The plane returns EXPLICIT null for unset caps (settings/handler.ts
    // settingsView) — `!= null` covers both null and absent, otherwise the
    // default no-cap state seeds the literal text "null" into the input.
    setCapText(s.tokensPerNight != null ? String(s.tokensPerNight) : "");
    setWinStart(s.nightWindowStart ?? DEFAULT_WINDOW_START);
    setWinEnd(s.nightWindowEnd ?? DEFAULT_WINDOW_END);
    setPause(s.pauseWhenSeatLimited ?? true); // plane default true (§B)
    setPerSessionText(
      s.perSessionMaxTokens != null ? String(s.perSessionMaxTokens) : ""
    );
  }, []);

  const refresh = useCallback(
    async (reseed: boolean) => {
      try {
        const res = await getSettings();
        setLoaded(res);
        setHidden(false);
        if (reseed) seed(res);
      } catch {
        // Older plane (no /settings route) or transient error before the
        // first load — hide the surface rather than render dead controls.
        setLoaded((cur) => {
          if (cur === null) setHidden(true);
          return cur;
        });
      }
    },
    [seed, tenantId] // eslint-disable-line react-hooks/exhaustive-deps -- tenant rides every call; switching tenants must refetch
  );

  useEffect(() => {
    setLoaded(null);
    setHidden(false);
    setSaveErr(null);
    setSavedFlash(false);
    void refresh(true);
    const id = setInterval(() => void refresh(false), POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (!savedFlash) return;
    const t = window.setTimeout(() => setSavedFlash(false), 2500);
    return () => window.clearTimeout(t);
  }, [savedFlash]);

  if (hidden || loaded === null) return null;

  const saved = loaded.settings;
  const tz = saved.timezone?.trim() || DEFAULT_TIMEZONE;

  // ── Tonight budget progress bar — SAVED settings, live usage ──────
  const win = currentNightWindow(
    saved.nightWindowStart,
    saved.nightWindowEnd,
    tz
  );
  const planeUsage = loaded.usage?.windowTokens;
  const approx =
    planeUsage === undefined
      ? windowSessionTokens(sessions ?? [], win)
      : null;
  const used = planeUsage ?? approx?.tokens ?? 0;

  // ── Per-service split (phase 12) ───────────────────────────────────
  // Prefer the plane's byService rollup; otherwise fall to the session-row
  // approximation split by runtime. A codex-capable machine surfaces the
  // split even at zero usage; a Claude-only tenant sees no Codex noise.
  const split = loaded.usage?.byService ?? approx?.byRuntime ?? {};
  const codexCapable = (machines ?? []).some((m) =>
    machineRuntimes(m).includes("codex")
  );
  const splitRows = RUNTIME_ORDER.filter((rt) => {
    const v = split[rt] ?? 0;
    if (rt === "claude") return true; // always anchor the Claude figure
    if (v > 0) return true; // a real codex/any figure
    return rt === "codex" && codexCapable; // codex machine, no usage yet
  }).map((rt) => ({ rt, tokens: split[rt] ?? 0 }));
  // Only a multi-service view is worth showing — a lone "Claude X" line just
  // restates the total below it.
  const showSplit = splitRows.length > 1;
  const cap = saved.tokensPerNight; // number | null (null = no cap)
  const over = cap != null && cap > 0 && used >= cap;
  const pct =
    cap != null && cap > 0
      ? Math.min(100, Math.round((used / cap) * 100))
      : 0;

  // ── Edit-buffer parsing / dirty detection ──────────────────────────
  const capParsed = parseTokens(capText);
  const perSessionParsed = parseTokens(perSessionText);
  const invalid =
    capParsed === null
      ? "tokens-per-night must be a number (e.g. 1500000, 1.5m, 800k)"
      : perSessionParsed === null
        ? "per-session cap must be a number (e.g. 200k)"
        : null;
  // "No cap" is `undefined` from parseTokens("") but `null` from the plane —
  // canonicalize both sides to null so an empty input over a null cap isn't
  // permanently dirty.
  const dirty =
    (capParsed ?? null) !== (saved.tokensPerNight ?? null) ||
    winStart !== (saved.nightWindowStart ?? DEFAULT_WINDOW_START) ||
    winEnd !== (saved.nightWindowEnd ?? DEFAULT_WINDOW_END) ||
    pause !== (saved.pauseWhenSeatLimited ?? true) ||
    (perSessionParsed ?? null) !== (saved.perSessionMaxTokens ?? null);

  const save = async () => {
    if (saving || !dirty || invalid) return;
    setSaving(true);
    setSaveErr(null);
    try {
      // Whole-doc PUT: fields we don't edit (timezone, notifyOnBudget)
      // ride through unchanged. Caps are EXPLICIT — number to set, null to
      // clear — because the plane's PUT is a partial update that only
      // removes a cap on explicit null (settings/handler.ts); omitting a
      // cleared cap would silently leave the old cap in place. The
      // `invalid` guard above means parseTokens' unparseable-null can't
      // reach here, so `?? null` only converts empty-input undefined.
      const next: TenantSettings = {
        tokensPerNight: capParsed ?? null,
        nightWindowStart: winStart,
        nightWindowEnd: winEnd,
        ...(saved.timezone ? { timezone: saved.timezone } : {}),
        pauseWhenSeatLimited: pause,
        perSessionMaxTokens: perSessionParsed ?? null,
        ...(saved.notifyOnBudget !== undefined
          ? { notifyOnBudget: saved.notifyOnBudget }
          : {}),
      };
      await putSettings(next);
      setSavedFlash(true);
      await refresh(true);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : "save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <SecLabel
        action={
          <span
            className="mono"
            style={{
              fontSize: 10.5,
              color: "var(--color-fg-4)",
              whiteSpace: "nowrap",
            }}
          >
            window {windowLabel(saved.nightWindowStart, saved.nightWindowEnd)} ·{" "}
            {tz}
          </span>
        }
      >
        Tonight's budget
      </SecLabel>
      <Card>
        {/* progress bar — window usage vs cap */}
        {cap != null && cap > 0 ? (
          <div>
            <div
              className="dsh-budget-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={cap}
              aria-valuenow={Math.min(used, cap)}
              aria-label="night window token usage"
            >
              <span
                className={`dsh-budget-fill${over ? " over" : ""}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="dsh-budget-row">
              <span className="mono">
                {compactCount(used)} of {compactCount(cap)} tokens · window{" "}
                {win.active ? "open" : "closed"}
              </span>
              {over && <StatusPill status="hold-budget" />}
            </div>
            <p className="hint" style={{ marginTop: 6, marginBottom: 0 }}>
              {planeUsage !== undefined
                ? "usage from the plane's seat rollups (input+output, cache excluded)"
                : `estimated from ${approx?.sessions ?? 0} session${
                    (approx?.sessions ?? 0) === 1 ? "" : "s"
                  } started this window — whole-session input+output totals over the newest rows only, so it's a lower bound`}
            </p>
          </div>
        ) : (
          <p className="hint" style={{ margin: 0 }}>
            No nightly cap set — overnight dispatch never pauses for budget.
            Set one below to make the swarm stop before it eats the week.
          </p>
        )}

        {/* phase 12 §T9: per-service Claude/Codex split — only when more than
            one service is in play (a Claude-only tenant just sees the total
            above). Uses the plane's byService rollup, or the session-row
            approximation split by runtime. */}
        {showSplit && (
          <div
            className="dsh-budget-row"
            style={{ marginTop: cap != null && cap > 0 ? 8 : 12 }}
            title="window usage split by coding-agent service"
          >
            <span className="mono">
              {splitRows
                .map((r) => `${RUNTIME_LABEL[r.rt]} ${compactCount(r.tokens)}`)
                .join(" · ")}
            </span>
          </div>
        )}

        <div
          className="dsh-form"
          style={{ gap: 14, marginTop: 16 }}
        >
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 160px", minWidth: 0 }}>
              <span className="dsh-field-label">Tokens per night</span>
              <input
                className="input mono"
                placeholder="e.g. 1.5m — empty = no cap"
                value={capText}
                onChange={(e) => setCapText(e.target.value)}
              />
              <p className="hint" style={{ marginTop: 6, marginBottom: 0 }}>
                pause overnight dispatch after this much usage
              </p>
            </div>
            <div style={{ flex: "1 1 160px", minWidth: 0 }}>
              <span className="dsh-field-label">Per-session cap</span>
              <input
                className="input mono"
                placeholder="e.g. 200k — empty = none"
                value={perSessionText}
                onChange={(e) => setPerSessionText(e.target.value)}
              />
              <p className="hint" style={{ marginTop: 6, marginBottom: 0 }}>
                alerts when one session crosses this — it does not kill the
                session
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 120px", minWidth: 0 }}>
              <span className="dsh-field-label">Night window start</span>
              <input
                className="input mono"
                type="time"
                value={winStart}
                onChange={(e) => setWinStart(e.target.value)}
              />
            </div>
            <div style={{ flex: "1 1 120px", minWidth: 0 }}>
              <span className="dsh-field-label">Night window end</span>
              <input
                className="input mono"
                type="time"
                value={winEnd}
                onChange={(e) => setWinEnd(e.target.value)}
              />
            </div>
          </div>
          <p className="hint" style={{ margin: 0 }}>
            the cap only governs work dispatched inside this window ({tz});
            "always run" schedules are exempt
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Toggle
              on={pause}
              onClick={() => setPause((p) => !p)}
              aria-label={
                pause
                  ? "disable pause when seat limited"
                  : "enable pause when seat limited"
              }
            />
            <span style={{ fontSize: 13, color: "var(--color-fg-2)" }}>
              pause when seat limited
              <span className="hint" style={{ display: "block", fontSize: 12 }}>
                hold dispatch while a machine's Claude account reports a usage
                limit — machines on a healthy seat still take "any" work
              </span>
            </span>
          </div>

          {/* §B2 honesty copy — estimates, shared weekly caps */}
          <p
            className="hint"
            style={{
              margin: 0,
              fontSize: 12,
              borderTop: "1px solid var(--color-line-1)",
              paddingTop: 10,
            }}
          >
            Honest numbers: these are estimates from session telemetry, not
            Anthropic's meter, and your allowance here is your own guess.
            Weekly caps are shared with ALL your Claude usage — claude.ai
            chats included — so NightSwarm's view is a lower bound. The hard
            truth is the seat-limit detection when it fires.
          </p>

          {(invalid || saveErr) && (
            <span className="dsh-hint-error">{invalid ?? saveErr}</span>
          )}

          <div className="dsh-form-actions">
            {savedFlash && <StatusPill status="delivered">saved</StatusPill>}
            <Btn
              variant="primary"
              onClick={() => void save()}
              disabled={saving || !dirty || !!invalid}
            >
              {saving ? "Saving…" : "Save budget"}
            </Btn>
          </div>
        </div>
      </Card>
    </div>
  );
}
